import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Inject,
  Input,
  NgModule,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {ENABLE_DETAIL_VIEW, SHOW_DESCRIPTION_PREVIEW} from '../../providers/storeFront.providers';
import {Observable, ReplaySubject} from 'rxjs';
import {CommonModule} from '@angular/common';
import {ProductListItemModule} from '../product-list-item/product-list-item.component';
import {Category} from '../../model/product-category';
import {Product} from '../../model/product';
import {RouterModule} from '@angular/router';
import {GetProductByIdPipeModule} from '../../store/catalog-product/get-product-by-id.pipe';
import {ReactiveComponentModule} from '@ngrx/component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IS_XS} from "../../providers/ui.providers";

export type ProductListMode = 'preview' | 'full';

@UntilDestroy()
@Component({
  selector: 'app-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  @Input()
  products: Product[];

  @Input()
  category: Category;

  @Input()
  listMode: ProductListMode = 'full';

  @Output()
  displayListUpdated: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  productClick: EventEmitter<any> = new EventEmitter<any>();

  products$: ReplaySubject<Product[]> = new ReplaySubject<Product[]>(1);

  private built: boolean;

  private creationInterval;

  @ViewChild('itemsContainer', {read: ViewContainerRef}) container: ViewContainerRef;
  @ViewChild('productListItem', {read: TemplateRef}) template: TemplateRef<any>;
  @ViewChild('previewLastListItem', {read: TemplateRef}) lastItemTemplate: TemplateRef<any>;

  constructor(
    @Inject(ENABLE_DETAIL_VIEW) public readonly enableDetailView$: Observable<boolean>,
    @Inject(SHOW_DESCRIPTION_PREVIEW) public showDescriptionPreview$: Observable<boolean>,
    @Inject(IS_XS) public isXs$: Observable<boolean>,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnDestroy(): void {
    clearInterval(this.creationInterval);
    this.container.clear();
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.products$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(products => {
        clearInterval(this.creationInterval);
        if (this.container) {
          this.container.clear();
        }
        this.buildProducts(products);
      });
  }

  trackByFn(index, item) {
    return item?.id;
  }

  // https://blog.bitsrc.io/3-ways-to-render-large-lists-in-angular-9f4dcb9b65
  buildProducts(products: Product[]) {
    if (products?.length > 0 && this.container) {

      const ITEMS_RENDERED_AT_ONCE = 20;
      const INTERVAL_IN_MS = 0;

      let currentIndex = 0;

      this.creationInterval = setInterval(() => {
        const nextIndex = currentIndex + ITEMS_RENDERED_AT_ONCE;
        let viewRef: EmbeddedViewRef<any>;
        for (let i = currentIndex; i < nextIndex; i++) {

          const context = {
            product: products[i],
            isLast: i === products.length - 1
          };
          viewRef = this.container.createEmbeddedView(this.template, context);
          viewRef.markForCheck();

          if (i >= products.length - 1) {
            clearInterval(this.creationInterval);
            if (this.listMode === 'preview' && products.length < this.products.length) {
              viewRef = this.container.createEmbeddedView(this.lastItemTemplate, context);
              viewRef.markForCheck();
            }
            break;
          }
        }

        this.cdr.detectChanges();

        this.displayListUpdated.emit();

        currentIndex += ITEMS_RENDERED_AT_ONCE;

      }, INTERVAL_IN_MS);

    }
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (this.products && this.listMode) {
      if (this.listMode === 'full') {
        this.products$.next(this.products);
      } else {
        this.products$.next(this.products.slice(0, 3));
      }
    }

  }

}

@NgModule({
  declarations: [
    ProductListComponent
  ],
  imports: [
    CommonModule,
    ProductListItemModule,
    RouterModule,
    GetProductByIdPipeModule,
    ReactiveComponentModule
  ],
  exports: [
    ProductListComponent
  ],
  entryComponents: []
})

export class ProductListModule {
}
